import type { TCatFieldName, TCatFormDataInput } from "@./suf-store";
import type {
  ValidatableValue,
} from "libs/utils/validators/src/lib/validationRules";

import { getNestedFieldValue, updateNestedField } from "./nestedFieldUtils";

/** Default answers for a cat, keyed by dot-notated (`TCatFieldName`) path. */
export type TCatDefaultValues = Partial<Record<TCatFieldName, ValidatableValue>>;

/**
 * Fills in default values for any of the given fields that are still `undefined`.
 * Fields that already have a value (including `false` or `[]`) are left untouched.
 *
 * @param cat - The cat to apply defaults to.
 * @param defaults - A map of dot-notated (`TCatFieldName`) paths to their default values.
 * @returns The updated cat and the fields that were filled in.
 *
 * @example
 * applyCatDefaults(cat, { "medical.hasConditions": false });
 * // { cat: { ...cat, medical: { ...cat.medical, hasConditions: false } }, appliedFields: ["medical.hasConditions"] }
 */
export function applyCatDefaults(
  cat: TCatFormDataInput,
  defaults: TCatDefaultValues,
): { cat: TCatFormDataInput; appliedFields: TCatFieldName[] } {
  let updatedCat = cat;
  const appliedFields: TCatFieldName[] = [];

  const fieldNames = Object.keys(defaults).filter((key): key is TCatFieldName => key in defaults);

  for (const fieldName of fieldNames) {
    if (getNestedFieldValue(updatedCat, fieldName) === undefined) {
      updatedCat = updateNestedField(updatedCat, fieldName, defaults[fieldName]);
      appliedFields.push(fieldName);
    }
  }

  return { cat: updatedCat, appliedFields };
}
